import React, { useCallback, useEffect, useRef } from 'react';
import { getFrameSourceRect } from 'core/slices/utils/imageFraming';

// Duration of the crop transition when the framing mode changes.
const FRAME_ANIM_MS = 220;

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function lerpRect(a, b, t) {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    width: a.width + (b.width - a.width) * t,
    height: a.height + (b.height - a.height) * t,
  };
}

// Paint one frame: draw the normalized source rect to fill the canvas ("contain"
// fit) and stroke the bbox relative to that same crop. Decodes nothing — the
// image is already loaded — so this is cheap enough to call every rAF tick.
function paintFrame(canvas, img, rect, metadata, type, strokeWidth, highDpi) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  if (!nw || !nh) return;
  const dpr = highDpi ? (window.devicePixelRatio || 1) : 1;
  const w = canvas.width / dpr;
  const h = canvas.height / dpr;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.save();
  if (highDpi) ctx.scale(dpr, dpr);

  const sx = rect.x * nw;
  const sy = rect.y * nh;
  const sw = rect.width * nw;
  const sh = rect.height * nh;

  const scale = Math.min(w / sw, h / sh);
  const drawW = sw * scale;
  const drawH = sh * scale;
  const drawX = (w - drawW) / 2;
  const drawY = (h - drawH) / 2;

  ctx.drawImage(img, sx, sy, sw, sh, drawX, drawY, drawW, drawH);

  const x1 = metadata?.x1;
  const y1 = metadata?.y1;
  const x2 = metadata?.x2;
  const y2 = metadata?.y2;
  if (x1 != null && y1 != null && x2 != null && y2 != null) {
    ctx.strokeStyle = type === 'overhead' ? '#ffa400' : '#e91e63';
    ctx.lineWidth = strokeWidth;
    ctx.strokeRect(
      (x1 - sx) * scale + drawX,
      (y1 - sy) * scale + drawY,
      (x2 - x1) * scale,
      (y2 - y1) * scale,
    );
  }
  ctx.restore();
}

// Fixed size mode: pass width + height
// Responsive mode: omit width/height, canvas fills its container
// highDpi: render at devicePixelRatio resolution (use for fullscreen/zoom contexts)
// frameMode: 'auto' (crop overhead toward the detection box) | 'full' (no crop), default 'full'
export default function TaskImageCanvas({
  src,
  metadata,
  type,
  width,
  height,
  strokeWidth = 1,
  highDpi = false,
  frameMode = 'full',
  onDrawn,
  onError,
}) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);   // loaded HTMLImageElement
  const rectRef = useRef(null);  // last painted normalized source rect
  const rafRef = useRef(0);

  // Latest paint params, read by imperative paints/tweens so those callbacks
  // stay stable and mode/metadata changes never trigger an image reload.
  const paramsRef = useRef({});
  paramsRef.current = { metadata, type, strokeWidth, highDpi, frameMode };

  const onDrawnRef = useRef(onDrawn);
  onDrawnRef.current = onDrawn;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const stableOnDrawn = useCallback(() => onDrawnRef.current?.(), []);
  const stableOnError = useCallback(() => onErrorRef.current?.(), []);

  const responsive = width == null || height == null;

  // Resize the canvas backing store to its CSS box (× dpr). Returns false when
  // the target size is not measurable yet (e.g. hidden container).
  const syncCanvasSize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    const dpr = paramsRef.current.highDpi ? (window.devicePixelRatio || 1) : 1;
    if (responsive) {
      const parent = canvas.parentElement;
      if (!parent) return false;
      const cw = parent.offsetWidth;
      const ch = parent.offsetHeight;
      if (cw <= 0 || ch <= 0) return false;
      canvas.width = cw * dpr;
      canvas.height = ch * dpr;
    } else {
      const physW = width * dpr;
      const physH = height * dpr;
      if (canvas.width !== physW) canvas.width = physW;
      if (canvas.height !== physH) canvas.height = physH;
    }
    return true;
  }, [responsive, width, height]);

  const paintRect = useCallback((rect) => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !rect) return;
    const { metadata, type, strokeWidth, highDpi } = paramsRef.current;
    paintFrame(canvas, img, rect, metadata, type, strokeWidth, highDpi);
    rectRef.current = rect;
  }, []);

  const cancelTween = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
  }, []);

  const animateToRect = useCallback((target) => {
    cancelTween();
    const from = rectRef.current;
    if (!from) { paintRect(target); return; }
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / FRAME_ANIM_MS);
      paintRect(lerpRect(from, target, easeOutCubic(t)));
      rafRef.current = t < 1 ? requestAnimationFrame(step) : 0;
    };
    rafRef.current = requestAnimationFrame(step);
  }, [cancelTween, paintRect]);

  // Load the image when src changes. First paint is instant (no tween).
  useEffect(() => {
    cancelTween();
    imgRef.current = null;
    rectRef.current = null;
    if (!src) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      if (!img.naturalWidth || !img.naturalHeight) return;
      imgRef.current = img;
      if (!syncCanvasSize()) return;
      const { metadata, frameMode } = paramsRef.current;
      paintRect(getFrameSourceRect(frameMode, metadata, img.naturalWidth, img.naturalHeight));
      stableOnDrawn();
    };
    img.onerror = () => {
      console.warn('[TaskImageCanvas] failed to load:', src);
      if (!cancelled) stableOnError();
    };
    img.src = src;
    return () => { cancelled = true; cancelTween(); };
  }, [src, syncCanvasSize, paintRect, cancelTween, stableOnDrawn, stableOnError]);

  // Animate to the new crop when the framing mode (or bbox) changes, once loaded.
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    animateToRect(getFrameSourceRect(frameMode, metadata, img.naturalWidth, img.naturalHeight));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameMode, metadata]);

  // Repaint the current crop when stroke params change (no motion).
  useEffect(() => {
    if (!imgRef.current || !rectRef.current) return;
    syncCanvasSize();
    paintRect(rectRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, strokeWidth, highDpi]);

  // Responsive: repaint the current crop when the container resizes.
  useEffect(() => {
    if (!responsive) return;
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    if (!parent) return;
    const observer = new ResizeObserver(() => {
      const img = imgRef.current;
      if (!img || !syncCanvasSize()) return;
      const rect = rectRef.current
        || getFrameSourceRect(paramsRef.current.frameMode, paramsRef.current.metadata, img.naturalWidth, img.naturalHeight);
      paintRect(rect);
    });
    observer.observe(parent);
    return () => observer.disconnect();
  }, [responsive, syncCanvasSize, paintRect]);

  // Fixed size: resync the backing store when width/height change, then repaint.
  useEffect(() => {
    if (responsive || !imgRef.current) return;
    if (syncCanvasSize()) paintRect(rectRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height]);

  if (responsive) {
    return <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />;
  }
  // CSS size stays at logical pixels; bitmap size is set via JS (physical px when highDpi)
  return <canvas ref={canvasRef} style={{ display: 'block', width, height, maxWidth: '100%' }} />;
}
